import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import { FieldPanel, FormHeader, LineBreak, tooltipStyles } from '../TemplateForm/styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  AllLogsTemplatesByTypeDocument,
  Equation,
  ExpressionType,
  GetVersionsDocument,
  LogTemplateType,
  LogsTemplateQuery,
  SectionUnitsEnum,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
  LogsTemplateField,
} from '@/__generated__/graphql';
import { keyFromTitle, useSnackBarMessage, cleanEquations, removeTypename } from '../utils';
import { formatEnumForDisplay } from '@/utils';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { PageLoader } from '@/components/shared/PageLoader';
import { EquationsConfigTab } from './KPIDerivedFieldsForm/EquationsConfigTab';

type QaqcEquationsPanelProps = {
  structureId: string;
  logsTemplate: LogsTemplateQuery['logsTemplate'] | null | undefined;
};

export const QaqcEquationsPanel = ({ structureId, logsTemplate }: QaqcEquationsPanelProps) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [equations, setEquations] = useState<Equation[]>([]);
  const [originalEquations, setOriginalEquations] = useState<Equation[]>([]);
  const [equationDerivedFieldConfigByIndex, setEquationDerivedFieldConfigByIndex] = useState<
    Record<number, { fieldUnit: SectionUnitsEnum | null; displayInLogAggregation: boolean }>
  >({});

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const templateLabel = formatEnumForDisplay(LogTemplateType.QaqcTemplate);
  const hostLogsTemplateId = logsTemplate?.id;

  const [updateLogsTemplateForDerivedField] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const qaqcFieldKeySet = useMemo(() => {
    return new Set(
      (logsTemplate?.fields ?? [])
        .map((field) => field?.key)
        .filter((key): key is string => Boolean(key))
    );
  }, [logsTemplate?.fields]);

  const availableFieldKeys = useMemo(() => {
    const keys = new Set<string>();
    const ordered: string[] = [];
    for (const field of logsTemplate?.fields ?? []) {
      const key = field?.key;
      if (key && !keys.has(key)) {
        keys.add(key);
        ordered.push(key);
      }
    }
    return ordered;
  }, [logsTemplate?.fields]);

  const availableFieldsForEquations = useMemo(() => {
    return (logsTemplate?.fields ?? []).filter(
      (field): field is LogsTemplateField => field !== null
    );
  }, [logsTemplate?.fields]);

  const templateEquationsSnapshot = useMemo(() => {
    return logsTemplate?.equations?.filter((equation) => equation !== null) ?? [];
  }, [logsTemplate?.equations]);

  useEffect(() => {
    if (!logsTemplate || mode !== 'ReadOnly') return;

    const filteredEquations = logsTemplate.equations?.filter((equation) => equation !== null) || [];

    setEquations(filteredEquations);
    setOriginalEquations(filteredEquations);
  }, [logsTemplate, mode]);

  const handleEquationsChange = useCallback((nextEquations: Equation[]) => {
    setEquations(nextEquations);
  }, []);

  const handleCreateNewField = useCallback(
    (
      fieldKey: string,
      equationKey: string,
      config?: { fieldUnit?: SectionUnitsEnum | null; displayInLogAggregation?: boolean }
    ) => {
      if (!logsTemplate?.fields || !hostLogsTemplateId) return;

      const existingField = logsTemplate.fields.find((field) => field.key === fieldKey);
      if (existingField) return;

      const newField: LogsTemplateField = {
        id: `newField_${Date.now()}`,
        key: fieldKey,
        name: fieldKey.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()),
        type: TemplateFieldTypeEnum.Input,
        options: [],
        isReadOnly: false,
        isRequired: false,
        displayInReports: true,
        displayInReportFilters: false,
        fieldUnit: config?.fieldUnit ?? SectionUnitsEnum.Na,
        displayInLogAggregation: config?.displayInLogAggregation ?? true,
        isDerived: true,
        derivedEquationKey: equationKey,
        serverSide: false,
        displayConfig: {
          id: `display_${Date.now()}`,
          textFormat: TextFormatEnum.None,
          isSortable: true,
          sortType: SortTypeEnum.Number,
          type: TableCellStyleEnum.Text,
        },
      };

      const updatedFields = [...logsTemplate.fields, newField];

      updateLogsTemplateForDerivedField({
        variables: {
          input: {
            structureId,
            templateId: hostLogsTemplateId,
            fields: updatedFields,
            lastModifiedFor: 'ADD_DERIVED_FIELD',
          },
        },
        refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
      });

      showSnackBar(`Created new derived field: ${newField.name}`, 'success');
    },
    [
      logsTemplate?.fields,
      hostLogsTemplateId,
      structureId,
      updateLogsTemplateForDerivedField,
      showSnackBar,
    ]
  );

  const createFirstEquation = useCallback(() => {
    const newEquation: Equation = {
      key: keyFromTitle('New Equation'),
      name: 'New Equation',
      variables: [],
      expression: {
        type: ExpressionType.Number,
        value: 0,
      },
    };
    setEquations([newEquation]);
    setMode('Edit');
  }, []);

  const [saveEquations, { loading: isSaving }] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar(`An error occurred while updating ${templateLabel}`, 'error');
        return;
      }
      showSnackBar(`${templateLabel} equations saved successfully`, 'success');
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
      setMode('ReadOnly');
    },
  });

  const handleCancel = useCallback(() => {
    setEquations([...originalEquations]);
    setMode('ReadOnly');
  }, [originalEquations]);

  const handleSave = useCallback(() => {
    const equationKeys = equations
      .map((equation) => equation.key?.trim())
      .filter((key): key is string => Boolean(key));
    const equationKeyCounts = new Map<string, number>();
    for (const key of equationKeys) {
      equationKeyCounts.set(key, (equationKeyCounts.get(key) ?? 0) + 1);
    }
    const hasDuplicateEquationKey = [...equationKeyCounts.values()].some((count) => count > 1);
    if (hasDuplicateEquationKey) {
      showSnackBar(
        'Equation keys must be unique. Please rename the equation(s) with duplicate keys.',
        'error'
      );
      return;
    }

    const cleanedEquations = cleanEquations(equations);

    const titleCaseFromKey = (key: string) =>
      key
        .replaceAll('_', ' ')
        .trim()
        .replaceAll(/\s+/g, ' ')
        .replaceAll(/\b\w/g, (letter) => letter.toUpperCase());

    const existingFields = (logsTemplate?.fields ?? []).filter(
      (field): field is LogsTemplateField => field !== null
    );
    const fieldsByKey = new Map(existingFields.map((field) => [field.key ?? '', field]));

    const nextFields: LogsTemplateField[] = [...existingFields];

    for (let index = 0; index < equations.length; index += 1) {
      const equation = equations[index];
      const equationKey = equation.key?.trim();
      if (!equationKey) continue;

      const config = equationDerivedFieldConfigByIndex[index];

      const fieldUnit = config?.fieldUnit ?? SectionUnitsEnum.Na;
      const displayInLogAggregation = config?.displayInLogAggregation ?? true;

      const existing = fieldsByKey.get(equationKey);

      if (existing) {
        fieldsByKey.set(equationKey, {
          ...existing,
          fieldUnit,
          displayInLogAggregation,
          isDerived: true,
          derivedEquationKey: existing.derivedEquationKey ?? equationKey,
        });
        continue;
      }

      const newField: LogsTemplateField = {
        id: `newField_${Date.now()}_${index}`,
        key: equationKey,
        name: titleCaseFromKey(equationKey),
        type: TemplateFieldTypeEnum.Numeric,
        options: [],
        isReadOnly: true,
        isRequired: false,
        displayInReports: true,
        displayInReportFilters: false,
        displayInLogAggregation,
        isDerived: true,
        derivedEquationKey: equationKey,
        serverSide: false,
        fieldUnit,
        displayConfig: {
          id: `display_${Date.now()}_${index}`,
          textFormat: TextFormatEnum.None,
          isSortable: false,
          sortType: SortTypeEnum.String,
          type: TableCellStyleEnum.Text,
        },
      };

      fieldsByKey.set(equationKey, newField);
      nextFields.push(newField);
    }

    const updatedFields = nextFields.map((field) => fieldsByKey.get(field.key ?? '') ?? field);
    const cleanedFields = removeTypename(updatedFields);

    saveEquations({
      variables: {
        input: {
          structureId,
          templateId: hostLogsTemplateId ?? '',
          lastModifiedFor: 'Update QA/QC Equations',
          equations: cleanedEquations,
          fields: cleanedFields,
        },
      },
      refetchQueries: [GetVersionsDocument, AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    });
  }, [
    equations,
    equationDerivedFieldConfigByIndex,
    saveEquations,
    structureId,
    hostLogsTemplateId,
    showSnackBar,
    logsTemplate?.fields,
    templateLabel,
  ]);

  if (!hostLogsTemplateId) {
    return null;
  }

  if (mode === 'ReadOnly' && templateEquationsSnapshot.length > 0 && equations.length === 0) {
    return (
      <Box sx={{ pt: 2 }}>
        <PageLoader />
      </Box>
    );
  }

  const showReadOnlyEmpty = mode === 'ReadOnly' && (!equations || equations.length === 0);

  if (showReadOnlyEmpty) {
    return (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <Stack spacing={3} alignItems="center">
            <EmptyStatePlaceholder type="template" message="No equations configured" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Create equations using fields on this QA/QC template
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={createFirstEquation}
              sx={containedButtonStyle}
            >
              Create first equation
            </Button>
          </Stack>
        </FieldPanel>
      </Box>
    );
  }

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          Equations
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit equations"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <EquationsConfigTab
        mode={mode}
        equations={equations}
        availableFieldKeys={availableFieldKeys}
        availableFields={availableFieldsForEquations}
        qaqcFieldKeys={qaqcFieldKeySet}
        hostLogTemplateType={LogTemplateType.QaqcTemplate}
        onEquationsChange={handleEquationsChange}
        onCreateNewField={handleCreateNewField}
        onEquationDerivedFieldConfigChange={(equationIndex, config) => {
          setEquationDerivedFieldConfigByIndex((previous) => ({
            ...previous,
            [equationIndex]: config,
          }));
        }}
        originalEquations={originalEquations}
      />
    </Stack>
  );
};
